import { Button } from "@workspace/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { useState } from "react";
import { CTAButton } from "../cta-button";
import { useModal } from "../providers/modal-provider";
import { modalIds } from "./constants";
import { EventNames, sendEvent } from "@/lib/utils/analytics";
import Link from "next/link";

export const NotifyMeModal = () => {
  const [email, setEmail] = useState("");
  const [valid, setValid] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");
  const { handleCloseModal, modalId } = useModal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailRegex =
    /^(?!.*\s)[A-Za-z0-9]+(?:\.[A-Za-z0-9]+)*@[A-Za-z0-9]+(?:\.[A-Za-z0-9]+)+$/;

  const isOpen = modalId === modalIds.notifyMe;

  const handleClose = () => {
    sendEvent(EventNames.NOTIFY_ME_MODAL_DISMISSED);
    handleCloseModal();
  };

  const handleCloseButtonClick = () => {
    sendEvent(EventNames.NOTIFY_SUCCESSFUL_MODAL_CROSS_BUTTON_CLICKED);
    handleCloseModal();
  };

  const handleNotify = async () => {
    if (!emailRegex.test(email)) {
      setValid(false);
      return;
    }

    setIsSubmitting(true);

    const response = await fetch("/api/rebrand/send-discount", {
      method: "POST",
      body: JSON.stringify({
        email: email,
      }),
    });

    const data = await response.json();

    setIsSubmitting(false);

    if (!response.ok) {
      setValid(false);
      setError(data.message);
      return;
    }
    sendEvent(EventNames.NOTIFY_MODAL_EMAIL_SUBMIT);
    setShowSuccess(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-[560px] border-0 mx-auto p-0 gap-0 fixed top-auto bottom-0 left-0 right-0 translate-y-0 translate-x-0 md:fixed md:top-[50%] md:left-[50%] md:translate-x-[-50%] md:translate-y-[-50%] md:bottom-auto md:right-auto md:overflow-hidden rounded-[0px]"
        showCloseButton={!showSuccess}
        closeButtonClick={handleCloseButtonClick}
      >
        {!showSuccess && (
          <div className="px-5 py-8 flex flex-col gap-4">
            <DialogHeader className="flex flex-col gap-3">
              <DialogTitle className="!title-32 text-left">
                We’ll notify you once the product is available
              </DialogTitle>
              <DialogDescription className="!body-16 text-left">
                Enter your email and we’ll let you know the moment it’s back.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <Input
                placeholder="Your Email*"
                type="email"
                className="border-0 border-b border-input rounded-none px-0 focus-visible:border-b-ring focus-visible:ring-0 aria-invalid:border-b-destructive aria-invalid:ring-0 placeholder:text-grey"
                onChange={(e) => {
                  setEmail(e.target.value);
                  setValid(emailRegex.test(e.target.value));
                }}
              />
              {!valid && (
                <p className="text-destructive body-16">
                  {error || "Invalid email address. Please enter a valid one."}
                </p>
              )}
            </div>
            <DialogFooter className="w-full flex flex-col gap-4 sm:flex-col">
              <CTAButton
                text="Notify me"
                textClassName="w-full !body-17"
                testId="notify-cta"
                handleCTAClick={handleNotify}
                className="md:w-full"
                containerClassName="w-full md:max-w-full"
                disabled={isSubmitting}
              />
              <p className="body-14 text-grey">
                By submitting your email, you agree to the&nbsp;
                <Link className="text-secondary" href="/terms">
                  Terms and Conditions
                </Link>{" "}
                and acknowledge the{" "}
                <Link className="text-secondary" href="/privacy-policy">Privacy Policy</Link>.
              </p>
            </DialogFooter>
          </div>
        )}
        {showSuccess && (
          <div className="px-5 py-8 flex flex-col gap-8">
            <DialogHeader className="flex flex-col gap-4">
              <div className="flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="64"
                  height="65"
                  viewBox="0 0 64 65"
                  fill="none"
                >
                  <circle
                    cx="32"
                    cy="32.6152"
                    r="30.72"
                    stroke="#232221"
                    strokeWidth="2.56"
                  />
                  <path
                    d="M16 31.3587C20.7257 35.848 23.3752 38.365 28.1008 42.8544L48 21.7344"
                    stroke="#232221"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="flex flex-col gap-2 items-center">
                <DialogTitle className="!title-32 text-center max-w-[340px]">
                  You're on the waitlist!
                </DialogTitle>
                <DialogDescription className="!body-16 text-center max-w-[396px]">
                  We’ll email you as soon as the product is back in stock
                </DialogDescription>
              </div>
            </DialogHeader>
            <DialogFooter>
              <div className="w-full">
                <CTAButton
                  text="Got it"
                  textClassName="w-full !body-16 !md:body-17"
                  testId="success-cta"
                  handleCTAClick={() => {
                    sendEvent(EventNames.NOTIFY_SUCCESSFUL_MODAL_DISMISSED);
                    handleCloseModal();
                    setShowSuccess(false);
                  }}
                  className="md:w-full"
                  containerClassName="w-full md:max-w-full"
                />
              </div>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
